import { useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { NavLink } from 'react-router-dom';
import { closeDrawer, openModal } from '../../actions/common.actions';
import { initializeContactForm } from '../../actions/contactForm.actions';
import icon_hamburger from '../../assets/icons/icon_hamburger_blue.svg';
import icon_close from '../../assets/icons/icon_cross.svg';
import sampark_logo from '../../assets/icons/sampark.svg';
import { MY_ACCOUNT, SETTINGS, LOGOUT } from '../../constants/menuOptions';
import { USER_MENU } from '../../constants/menus';
import * as MODAL_COMPONENTS from '../../constants/modals';
import { SIGNOUT } from '../../constants/routes';
import { useAppDispatch, useAppSelector } from '../../helpers/store';
import getMenuOptions from '../../util/getMenuOptions';
import { useHeaderTabs } from '../../v2/hooks/useHeaderTabs';
import { useNavPanelStyles } from './NavPanel.styles';

interface IMenuOption {
  name: string;
  value: string;
  disabled?: boolean;
}

/**
 * Mobile slide-out nav drawer, restyled to match sampark-new's mobile sidebar
 * sheet (see docs/plans/2026-07-22-legacy-mobile-nav-parity-design.md).
 * Tab list, Yog Admin gating, and newAppUrl handling all come from
 * useHeaderTabs — the same source the desktop SideNav uses.
 */
const NavPanel = ({ history, location }: RouteComponentProps) => {
  const dispatch = useAppDispatch();
  const tabValues = useHeaderTabs();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { classes, cx } = useNavPanelStyles();
  const { userAccessObject, userAccount } = useAppSelector((state) => ({
    userAccessObject: state.userAccess.userAccessObject,
    userAccount: state.userAccess.userAccount
  }));

  const handleClose = () => dispatch(closeDrawer());

  const handleMenuOption = (option: IMenuOption) => {
    switch (option.value) {
      case MY_ACCOUNT.value:
        dispatch(initializeContactForm(userAccount));
        dispatch(openModal(MODAL_COMPONENTS.CONTACT_INFO_READ_ONLY));
        break;
      case SETTINGS.value:
        break;
      case LOGOUT.value:
        history.push(SIGNOUT.value);
        break;
      default:
    }
    handleClose();
  };

  const menuOptions: IMenuOption[] = getMenuOptions(USER_MENU, {
    pathname: location.pathname,
    userAccessObject
  }).filter((option: IMenuOption) => !option.disabled);

  const userName =
    `${userAccount?.firstName ?? ''} ${userAccount?.lastName ?? ''}`.trim() || 'User';
  const userInitials =
    `${userAccount?.firstName?.[0] ?? ''}${userAccount?.lastName?.[0] ?? ''}` || 'U';

  return (
    <div className={classes.panel}>
      <div className={classes.topRow}>
        <button aria-label="Menu" className={classes.iconButton} onClick={handleClose}>
          <img alt="" className={classes.topIcon} src={icon_hamburger} />
        </button>
        <img alt="sampark" className={classes.wordmark} src={sampark_logo} />
        <button
          aria-label="Close"
          className={cx(classes.iconButton, classes.closeButton)}
          onClick={handleClose}
        >
          <img alt="" className={classes.topIcon} src={icon_close} />
        </button>
      </div>
      <div className={classes.navList}>
        {tabValues.map((tab: any) => {
          // Routes marked with `newAppUrl` live in sampark-new — use a plain anchor
          // so the browser does a full page navigation instead of a client-side
          // React Router transition (which would 404 in the legacy app).
          if (tab.newAppUrl) {
            const isActive = location.pathname.startsWith(tab.value);
            return (
              <a
                key={tab.newAppUrl}
                className={cx(classes.link, isActive && classes.activeLink)}
                href={tab.newAppUrl}
                onClick={handleClose}
              >
                <div className={classes.icon}>{tab.icon({ stroke: 'currentColor' })}</div>
                <span>{tab.name}</span>
              </a>
            );
          }
          return (
            <NavLink
              key={tab.value}
              activeClassName={classes.activeLink}
              className={classes.link}
              to={tab.value}
              onClick={handleClose}
            >
              <div className={classes.icon}>{tab.icon({ stroke: 'currentColor' })}</div>
              <span>{tab.name}</span>
            </NavLink>
          );
        })}
      </div>
      <div className={classes.bottomSection}>
        {isUserMenuOpen && (
          <div className={classes.userMenuCard}>
            {menuOptions.map((option) => (
              <div
                key={option.value}
                className={cx(
                  classes.userMenuItem,
                  option.value === LOGOUT.value && classes.signOutItem
                )}
                onClick={() => handleMenuOption(option)}
              >
                {option.value === LOGOUT.value ? 'Sign Out' : option.name}
              </div>
            ))}
          </div>
        )}
        <button className={classes.userRow} onClick={() => setIsUserMenuOpen((open) => !open)}>
          <span className={classes.avatar}>{userInitials}</span>
          <span className={classes.userName}>{userName}</span>
          {/* chevrons-up-down, same glyph as sampark-new's user row */}
          <svg
            aria-hidden
            className={classes.chevrons}
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="m7 15 5 5 5-5" />
            <path d="m7 9 5-5 5 5" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default withRouter(NavPanel);
